import { useEffect, useState } from 'react';
import { supabase, Article, SocialLink, HeaderSettings, MenuItem, FooterSettings } from '../lib/supabase';
import { Search, Menu, X, ChevronRight, Clock, Eye, Facebook, Send, Instagram, Share2 } from 'lucide-react';
import WidgetRenderer from './WidgetRenderer';

export default function PublicArticleView() {
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [recentArticles, setRecentArticles] = useState<Article[]>([]);
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [headerSettings, setHeaderSettings] = useState<HeaderSettings | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [footerSettings, setFooterSettings] = useState<FooterSettings | null>(null);

  useEffect(() => {
    updateMetaTags();
  }, [article]);

  function updateMetaTags() {
    if (!article) return;

    document.title = article.seo_title || article.title;

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', article.seo_description || article.excerpt);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = article.seo_description || article.excerpt;
      document.head.appendChild(meta);
    }

    if (article.seo_keywords) {
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', article.seo_keywords);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'keywords';
        meta.content = article.seo_keywords;
        document.head.appendChild(meta);
      }
    }

    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }

    if (article.is_canonical && article.canonical_url) {
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = article.canonical_url;
      document.head.appendChild(canonical);
    }
  }

  useEffect(() => {
    loadArticle();
    loadRecentArticles();
    loadSocialLinks();
    loadHeaderSettings();
    loadMenuItems();
    loadFooterSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const contentDiv = document.querySelector('.article-content');
    if (contentDiv) {
      const iframes = contentDiv.querySelectorAll('iframe');
      iframes.forEach((iframe) => {
        if (!iframe.hasAttribute('sandbox')) {
          iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox');
        }
        if (!iframe.hasAttribute('allow')) {
          iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
        }
      });
    }
  }, [article]);

  async function loadArticle() {
    const { data } = await supabase
      .from('articles')
      .select('*')
      .eq('status', 'published')
      .order('published_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (data) {
      setArticle(data);
      await supabase
        .from('articles')
        .update({ views_count: data.views_count + 1 })
        .eq('id', data.id);
    }
    setLoading(false);
  }

  async function loadRecentArticles() {
    const { data } = await supabase
      .from('articles')
      .select('id, title, slug, published_at, views_count')
      .eq('status', 'published')
      .order('published_at', { ascending: false })
      .limit(5);

    if (data) {
      setRecentArticles(data);
    }
  }

  async function loadSocialLinks() {
    const { data } = await supabase
      .from('social_links')
      .select('*')
      .eq('is_active', true)
      .order('display_order');

    if (data) {
      setSocialLinks(data);
    }
  }

  async function loadHeaderSettings() {
    const { data } = await supabase
      .from('header_settings')
      .select('*')
      .limit(1)
      .maybeSingle();

    if (data) {
      setHeaderSettings(data);
    }
  }

  async function loadMenuItems() {
    const { data } = await supabase
      .from('menu_items')
      .select('*')
      .eq('is_active', true)
      .order('display_order');

    if (data) {
      setMenuItems(data);
    }
  }

  async function loadFooterSettings() {
    const { data } = await supabase
      .from('footer_settings')
      .select('*')
      .limit(1)
      .maybeSingle();

    if (data) {
      setFooterSettings(data);
    }
  }

  function getIconComponent(iconName: string) {
    const icons: { [key: string]: any } = {
      facebook: Facebook,
      send: Send,
      instagram: Instagram,
      share2: Share2,
    };
    return icons[iconName.toLowerCase()] || Share2;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-900 border-t-transparent"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-gray-600">Статьи не найдены</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Open Sans', sans-serif" }}>
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-2 sm:py-3">
          <div className="max-w-7xl mx-auto px-4 flex justify-center items-center">
            <div className="flex gap-3 sm:gap-6 text-sm sm:text-base flex-wrap justify-center">
              {socialLinks.map((link) => {
                return (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-gray-200 transition-colors whitespace-nowrap"
                  >
                    {link.name}
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <div className="py-2 sm:py-3" style={{ backgroundColor: headerSettings?.background_color || '#FFE9FC' }}>
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                <a href={headerSettings?.logo_link || 'https://seo-nova.com'} className="flex-shrink-0">
                  <img
                    src={headerSettings?.logo_url || '/Logo 72 px_100Kb.jpg'}
                    alt={headerSettings?.logo_alt || 'Logo'}
                    title={headerSettings?.logo_title || ''}
                    className="object-contain h-12 sm:h-16 md:h-[72px] w-auto"
                  />
                  {headerSettings?.logo_description && (
                    <span className="sr-only">{headerSettings.logo_description}</span>
                  )}
                </a>
                <div className="min-w-0 flex-1">
                  <h1
                    className="font-bold mb-0.5 truncate"
                    style={{
                      color: headerSettings?.title_color || '#ff0000',
                      fontFamily: headerSettings?.title_font_family || "'Open Sans', sans-serif",
                      fontSize: 'clamp(16px, 3.5vw, ' + (headerSettings?.title_font_size || '24px') + ')'
                    }}
                  >
                    {headerSettings?.title_text || 'Ольга Сеонова'}
                  </h1>
                  <p
                    className="truncate"
                    style={{
                      fontSize: 'clamp(10px, 2vw, ' + (headerSettings?.subtitle_font_size || '14px') + ')',
                      color: headerSettings?.subtitle_color || '#374151'
                    }}
                  >
                    {headerSettings?.subtitle_text || 'Быстрые и простые рецепты'}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 flex-shrink-0 ml-2"
                style={{ color: headerSettings?.menu_color || '#000066' }}
                aria-label="Меню"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>

              <nav className="hidden lg:flex gap-6 items-center ml-6">
                {menuItems.map((item) => (
                  <a
                    key={item.id}
                    href={item.url}
                    className="font-semibold transition-colors whitespace-nowrap"
                    style={{
                      color: headerSettings?.menu_color || '#000066',
                      fontSize: headerSettings?.menu_font_size || '16px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = headerSettings?.menu_hover_color || '#ef4444'}
                    onMouseLeave={(e) => e.currentTarget.style.color = headerSettings?.menu_color || '#000066'}
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>

            {mobileMenuOpen && (
              <nav className="lg:hidden mt-4 pb-4 border-t pt-4">
                <div className="flex flex-col gap-3">
                  {menuItems.map((item) => (
                    <a
                      key={item.id}
                      href={item.url}
                      className="font-semibold py-2"
                      style={{
                        color: headerSettings?.menu_color || '#000066',
                        fontSize: headerSettings?.menu_font_size || '16px'
                      }}
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </nav>
            )}
          </div>
        </div>
      </header>

      <div className="bg-gray-50 py-2 sm:py-3 border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-600 overflow-x-auto">
            <a href="/" className="hover:text-red-600 whitespace-nowrap">Главная</a>
            <ChevronRight size={16} className="flex-shrink-0" />
            <a href="#recipes" className="hover:text-red-600 whitespace-nowrap">{article.category}</a>
            <ChevronRight size={16} className="flex-shrink-0" />
            <span className="text-gray-900 truncate">{article.title}</span>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-4 sm:py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          <aside className="space-y-4 sm:space-y-6 lg:order-1">
            <WidgetRenderer position="sidebar" />
          </aside>

          <article className="lg:col-span-2 lg:order-2">
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 md:p-8">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4" style={{ color: '#000066' }}>
                {article.title}
              </h1>

              <div className="flex flex-wrap gap-3 sm:gap-6 text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6 pb-4 sm:pb-6 border-b">
                <div className="flex items-center gap-1 sm:gap-2">
                  <Clock size={14} className="sm:w-4 sm:h-4" />
                  <span className="whitespace-nowrap">
                    {new Date(article.published_at!).toLocaleDateString('ru-RU', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                  <Eye size={14} className="sm:w-4 sm:h-4" />
                  <span className="whitespace-nowrap">{article.views_count} просмотров</span>
                </div>
              </div>

              <div
                className="article-content"
                style={{ color: '#000066', fontSize: 'clamp(16px, 3vw, 20px)', lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {article.tags.length > 0 && (
                <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t">
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((tag) => (
                      <a
                        key={tag}
                        href={`#tag-${tag}`}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold"
                        style={{ backgroundColor: '#FFE9FC', color: '#000066' }}
                      >
                        #{tag}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </div>
      </main>

      <footer className="mt-8 sm:mt-12">
        <div className="py-3 sm:py-4" style={{ backgroundColor: '#FFE9FC' }}>
          <div className="max-w-7xl mx-auto px-4">
            <p
              className="text-center text-xs sm:text-sm footer-content"
              style={{ color: '#000066' }}
              dangerouslySetInnerHTML={{ __html: footerSettings?.copyright_text || '' }}
            />
          </div>
        </div>
      </footer>
    </div>
  );
}
